import React, { useEffect, useState } from 'react';
import { SubmitStatus as SubmitStatusValue } from '../utils/submitTake';

const POPPINS = "'Poppins', sans-serif";
// After this long, reassure the candidate that the submit is still going.
const SLOW_SUBMIT_MS = 15000;
const SLOW_MESSAGE = 'This is taking longer than usual. Please keep this page open.';

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

interface SubmitStatusProps {
  status: SubmitStatusValue | null;
  startedAt: number | null;
  align?: 'left' | 'center' | 'right';
  marginTop?: string;
}

// VI-900: status line shown while an answer is being submitted, so a slow
// upload or save is never a silent spinner. The live region stays mounted (so
// screen readers announce the first update) and only announces phase changes,
// not every percent tick.
const SubmitStatus: React.FC<SubmitStatusProps> = ({ status, startedAt, align = 'left', marginTop }) => {
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    setIsSlow(false);
    if (startedAt === null) return;
    const remaining = SLOW_SUBMIT_MS - (Date.now() - startedAt);
    if (remaining <= 0) {
      setIsSlow(true);
      return;
    }
    const timer = setTimeout(() => setIsSlow(true), remaining);
    return () => clearTimeout(timer);
  }, [startedAt]);

  const phaseText = status
    ? status.phase === 'uploading' ? 'Uploading your answer…' : 'Saving your answer…'
    : '';
  const visibleText =
    status?.phase === 'uploading' && status.percent !== undefined
      ? `${phaseText} ${status.percent}%`
      : phaseText;
  const showSlow = Boolean(status) && isSlow;

  return (
    <>
      <span role="status" aria-live="polite" style={visuallyHidden}>
        {phaseText}
        {showSlow ? ` ${SLOW_MESSAGE}` : ''}
      </span>
      {status && (
        <div
          id="submit-answer-status"
          aria-hidden="true"
          style={{
            fontFamily: POPPINS,
            fontSize: '12px',
            lineHeight: '18px',
            color: '#6c757d',
            textAlign: align,
            marginTop,
          }}
        >
          <div>{visibleText}</div>
          {showSlow && <div>{SLOW_MESSAGE}</div>}
        </div>
      )}
    </>
  );
};

export default SubmitStatus;
